import { useEffect, useState, useCallback } from "react";
import api, { errMsg } from "../lib/api";
import { PageHeader, Card, Badge, Modal, Btn, Field, inputCls } from "../components/common";
import { toast } from "sonner";
import { RefreshCw, Eye, CheckCircle2, AlertTriangle, FileWarning } from "lucide-react";
import { fmtDate } from "../lib/format";

const STATUS_LABEL = { menunggu: "Menunggu Verifikasi", terverifikasi: "Terverifikasi", perlu_perbaikan: "Perlu Perbaikan" };
const TONE = { menunggu: "amber", terverifikasi: "emerald", perlu_perbaikan: "rose" };

export default function VerifikasiDokumen() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("menunggu");
  const [pulling, setPulling] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [fixDoc, setFixDoc] = useState(null);
  const [fixNote, setFixNote] = useState("");

  const load = useCallback(() => {
    api.get("/ajl/berkas", { params: status ? { status } : {} }).then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const pull = async () => {
    setPulling(true);
    try {
      const r = await api.post("/ajl/pull");
      if (r.data.skipped) toast.info("URL aplikasi AJL belum diset — penarikan dilewati");
      else toast.success(`Penarikan selesai: ${JSON.stringify(r.data)}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setPulling(false); }
  };

  const openPreview = async (d) => {
    setPreview(d); setPreviewUrl(null);
    try {
      const r = await api.get(`/ajl/berkas/${d.id}/file`, { responseType: "blob" });
      setPreviewUrl(URL.createObjectURL(r.data));
    } catch { toast.error("File tidak tersedia"); }
  };

  const verify = (d) => api.post(`/ajl/berkas/${d.id}/verify`)
    .then(() => { toast.success("Berkas terverifikasi"); load(); })
    .catch((e) => toast.error(errMsg(e)));

  const sendFix = () => {
    if (!fixNote.trim()) return toast.error("Alasan perbaikan wajib diisi");
    api.post(`/ajl/berkas/${fixDoc.id}/request-fix`, { note: fixNote })
      .then(() => { toast.success("Permintaan perbaikan dikirim"); setFixDoc(null); setFixNote(""); load(); })
      .catch((e) => toast.error(errMsg(e)));
  };

  return (
    <div data-testid="verifikasi-dokumen-page">
      <PageHeader title="Verifikasi Dokumen Aplikasi" subtitle="Berkas yang diunggah konsumen lewat aplikasi ABAD JAYA LIVING — ditarik otomatis tiap 5 menit" testid="verdok-header">
        <Btn onClick={pull} disabled={pulling} data-testid="verdok-pull-btn">
          <RefreshCw className={`w-4 h-4 ${pulling ? "animate-spin" : ""}`} /> {pulling ? "Menarik..." : "Tarik sekarang"}
        </Btn>
      </PageHeader>
      <div className="flex gap-2 mb-4 flex-wrap" data-testid="verdok-filters">
        {["menunggu", "terverifikasi", "perlu_perbaikan", ""].map((s) => (
          <button key={s} onClick={() => setStatus(s)} data-testid={`verdok-filter-${s || "semua"}`}
            className={`text-xs px-3.5 py-2 rounded-lg border transition-colors ${status === s ? "bg-emerald-600/10 text-emerald-300 border-emerald-500/50" : "text-slate-400 border-slate-800 hover:bg-slate-900"}`}>
            {s ? STATUS_LABEL[s] : "Semua"}
          </button>
        ))}
      </div>
      <div className="space-y-2" data-testid="verdok-list">
        {rows.length === 0 && <Card><p className="text-slate-500 text-sm">Tidak ada berkas pada status ini.</p></Card>}
        {rows.map((d) => (
          <Card key={d.id} className="!p-4" testid={`verdok-${d.id}`}>
            <div className="flex flex-wrap items-center gap-2">
              <FileWarning className="w-4 h-4 text-amber-400 shrink-0" />
              <span className="font-medium text-slate-200">{d.name || d.code}</span>
              <Badge tone={TONE[d.status] || "slate"}>{STATUS_LABEL[d.status] || d.status}</Badge>
              <span className="text-[11px] text-slate-500">{fmtDate((d.uploaded_at || d.pulled_at || "").slice(0, 10))}</span>
            </div>
            <div className="text-[11px] text-slate-500 mt-1">
              {(d.konsumen || {}).name || "-"} · {(d.konsumen || {}).email || "-"} · {(d.konsumen || {}).phone || "-"}
              {d.note && <span className="text-rose-300"> · catatan: {d.note}</span>}
            </div>
            <div className="flex gap-2 mt-3 flex-wrap">
              <Btn onClick={() => openPreview(d)} className="!py-1.5 !px-3 text-xs" data-testid={`verdok-preview-${d.id}`}><Eye className="w-3.5 h-3.5" /> Pratinjau</Btn>
              {d.status !== "terverifikasi" && (
                <Btn onClick={() => verify(d)} className="!py-1.5 !px-3 text-xs" data-testid={`verdok-verify-${d.id}`}><CheckCircle2 className="w-3.5 h-3.5" /> Verifikasi</Btn>
              )}
              {d.status !== "perlu_perbaikan" && (
                <Btn onClick={() => { setFixDoc(d); setFixNote(""); }} className="!py-1.5 !px-3 text-xs !bg-rose-600/20 !text-rose-300" data-testid={`verdok-fix-${d.id}`}><AlertTriangle className="w-3.5 h-3.5" /> Minta Perbaikan</Btn>
              )}
            </div>
          </Card>
        ))}
      </div>

      <Modal open={!!preview} onClose={() => { setPreview(null); setPreviewUrl(null); }} title={preview ? `Pratinjau — ${preview.name}` : ""} testid="verdok-preview-modal">
        {previewUrl ? (
          preview?.content_type?.includes("pdf") || preview?.file_name?.endsWith(".pdf") ? (
            <iframe src={previewUrl} className="w-full h-[70vh] rounded-lg bg-white" title="preview-pdf" data-testid="verdok-preview-frame" />
          ) : (
            <img src={previewUrl} alt="pratinjau" className="w-full rounded-lg" data-testid="verdok-preview-img" />
          )
        ) : <p className="text-slate-500 text-sm">Memuat file...</p>}
      </Modal>

      <Modal open={!!fixDoc} onClose={() => setFixDoc(null)} title={`Minta Perbaikan — ${fixDoc?.name || ""}`} testid="verdok-fix-modal">
        <Field label="Alasan perbaikan (wajib)">
          <textarea className={inputCls} rows={3} value={fixNote} onChange={(e) => setFixNote(e.target.value)} placeholder="Contoh: foto KTP buram, mohon unggah ulang" data-testid="verdok-fix-note" />
        </Field>
        <Btn onClick={sendFix} className="mt-4" data-testid="verdok-fix-submit">Kirim Permintaan Perbaikan</Btn>
      </Modal>
    </div>
  );
}
